import React, { useState, useEffect } from 'react'
import { Box, Button, Typography, Alert, CircularProgress } from '@mui/material'
import { collection, getDocs, doc, updateDoc, addDoc, deleteDoc, query, orderBy, serverTimestamp } from 'firebase/firestore'
import { db } from '../../firebase'

interface Product {
  id: string
  name: string
  category: string
  price: number
  isActive: boolean
  status: 'pending' | 'approved' | 'rejected'
  imageUrls: string[]
  createdAt: any
  updatedAt: any
}

export default function TestProductsPage() {
  const [products, setProducts] = useState<Product[]>([])
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState('')
  const [error, setError] = useState('')

  const loadProducts = async () => {
    try {
      setLoading(true)
      setError('')
      console.log('🔄 Starting to load products...')
      
      const productsRef = collection(db, 'products')
      const q = query(productsRef, orderBy('createdAt', 'desc'))
      const snapshot = await getDocs(q)
      
      const productsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Product[]
      
      setProducts(productsData)
      setMessage(`تم تحميل ${productsData.length} منتج بنجاح`)
      console.log('✅ Products loaded:', productsData.length)
    } catch (error) {
      console.error('❌ Error loading products:', error)
      setError('خطأ في تحميل المنتجات: ' + (error as Error).message)
    } finally {
      setLoading(false)
    }
  }

  const testUpdateProduct = async () => {
    if (products.length === 0) {
      setError('لا توجد منتجات للاختبار')
      return
    }

    try {
      setLoading(true)
      setError('')
      const product = products[0]
      console.log('🔄 Testing product update:', product.id)
      
      if (!product.id || product.id.trim() === '') {
        setError('معرف المنتج فارغ - لا يمكن التحديث')
        setLoading(false)
        return
      }
      
      await updateDoc(doc(db, 'products', product.id), {
        isActive: !product.isActive,
        updatedAt: serverTimestamp()
      })
      
      setMessage(`تم تحديث المنتج ${product.name} بنجاح`)
      console.log('✅ Product updated successfully')
      
      // إعادة تحميل المنتجات
      await loadProducts()
    } catch (error) {
      console.error('❌ Error updating product:', error)
      setError('خطأ في تحديث المنتج: ' + (error as Error).message)
    } finally {
      setLoading(false)
    }
  }

  const testCreateProduct = async () => {
    try {
      setLoading(true)
      setError('')
      console.log('🔄 Testing product creation...')
      
      const newProduct = {
        name: 'منتج اختبار',
        category: 'عام',
        price: 100,
        isActive: true,
        status: 'approved' as const,
        imageUrls: [],
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      }
      
      await addDoc(collection(db, 'products'), newProduct)
      
      setMessage('تم إنشاء منتج اختبار بنجاح')
      console.log('✅ Product created successfully')
      
      // إعادة تحميل المنتجات
      await loadProducts()
    } catch (error) {
      console.error('❌ Error creating product:', error)
      setError('خطأ في إنشاء المنتج: ' + (error as Error).message)
    } finally {
      setLoading(false)
    }
  }

  const testDeleteProduct = async () => {
    if (products.length === 0) {
      setError('لا توجد منتجات للاختبار')
      return
    }

    try {
      setLoading(true)
      setError('')
      const product = products[0]
      console.log('🔄 Testing product deletion:', product.id)
      
      if (!product.id || product.id.trim() === '') {
        setError('معرف المنتج فارغ - لا يمكن الحذف')
        setLoading(false)
        return
      }
      
      await deleteDoc(doc(db, 'products', product.id))
      
      setMessage(`تم حذف المنتج ${product.name} بنجاح`)
      console.log('✅ Product deleted successfully')
      
      // إعادة تحميل المنتجات
      await loadProducts()
    } catch (error) {
      console.error('❌ Error deleting product:', error)
      setError('خطأ في حذف المنتج: ' + (error as Error).message)
    } finally {
      setLoading(false)
    }
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        🧪 اختبار Firebase - إدارة المنتجات
      </Typography>
      
      {message && <Alert severity="success" sx={{ mb: 2 }}>{message}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <Button 
          variant="contained" 
          onClick={loadProducts}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : null}
        >
          تحميل المنتجات
        </Button>
        
        <Button 
          variant="outlined" 
          onClick={testCreateProduct}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : null}
        >
          إنشاء منتج اختبار
        </Button>
        
        <Button 
          variant="outlined" 
          onClick={testUpdateProduct}
          disabled={loading || products.length === 0}
          startIcon={loading ? <CircularProgress size={20} /> : null}
        >
          تحديث أول منتج
        </Button>
        
        <Button 
          variant="outlined" 
          color="error"
          onClick={testDeleteProduct}
          disabled={loading || products.length === 0}
          startIcon={loading ? <CircularProgress size={20} /> : null}
        >
          حذف أول منتج
        </Button>
      </Box>
      
      <Typography variant="h6" gutterBottom>
        المنتجات ({products.length})
      </Typography>
      
      {products.length === 0 ? (
        <Typography color="text.secondary">
          لا توجد منتجات. اضغط على "تحميل المنتجات" أو "إنشاء منتج اختبار"
        </Typography>
      ) : (
        <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
          {products.map((product, index) => (
            <Box key={product.id || `product-${index}`} sx={{ p: 2, border: '1px solid #ddd', mb: 1, borderRadius: 1 }}>
              <Typography variant="subtitle1">
                {index + 1}. {product.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                الفئة: {product.category} | السعر: {product.price} | 
                الحالة: {product.isActive ? 'نشط' : 'غير نشط'} | 
                الوضع: {product.status}
              </Typography>
              <Typography variant="caption" color="error">
                ID: {product.id || 'فارغ!'}
              </Typography>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  )
}
